import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { App, Avatar, Form, Input, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { loadDeveloperAvatar, updateDeveloperProfile } from "@/api/developer";
import CopyIcon from "@/assets/copy.svg?react";
import BrandButton from "@/components/BrandButton";
import Button from "@/components/Button";
import { IconBtn } from "@/components/IconBtn";
import {
  ensureDeveloperProfile,
  profileUserAvatar,
  toStoredProfile,
} from "@/lib/loadDeveloperProfile";
import { useUserStore } from "@/store";
import { ImageCropModal } from "@/components/ImageCrop";

const NAME_MAX = 50;
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const AVATAR_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

interface DeveloperFormValues {
  name: string;
  website: string;
}

function DeveloperInfo() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const applyDeveloperProfile = useUserStore((s) => s.applyDeveloperProfile);
  const developerProfile = useUserStore((s) => s.developerProfile);
  const profileStatus = useUserStore((s) => s.profileStatus);
  const [form] = Form.useForm<DeveloperFormValues>();
  const watchedName = Form.useWatch("name", form);
  const watchedWebsite = Form.useWatch("website", form);
  const name = watchedName ?? developerProfile?.name ?? "";
  const website = watchedWebsite ?? developerProfile?.website ?? "";
  const fileRef = useRef<HTMLInputElement>(null);
  const cropObjectUrl = useRef<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropMime, setCropMime] = useState("image/jpeg");

  const [avatar, setAvatar] = useState(
    () => developerProfile?.displayAvatar ?? "",
  );
  const [developerNo, setDeveloperNo] = useState(
    () => developerProfile?.developerNo ?? "",
  );
  const [loading, setLoading] = useState(() => profileStatus !== "ready");
  const [saving, setSaving] = useState(false);
  const savedAvatar = developerProfile?.displayAvatar ?? "";
  const unchanged =
    name.trim() === (developerProfile?.name ?? "").trim() &&
    website.trim() === (developerProfile?.website ?? "").trim() &&
    avatar === savedAvatar;
  const imAvatarRef = useRef(developerProfile?.imAvatarUrl ?? "");
  /** 仅本次未保存的预览。已保存头像的 blob 属于资料仓库，离开页面不能释放。 */
  const previewUrl = useRef<string | null>(null);
  const seededRef = useRef(false);
  const fromWorkbench = location.pathname === "/developer";

  const revokePreview = () => {
    const previous = previewUrl.current;
    const stored = useUserStore.getState().developerProfile?.displayAvatar;
    if (previous?.startsWith("blob:") && previous !== stored) {
      URL.revokeObjectURL(previous);
    }
    previewUrl.current = null;
  };

  const showAvatar = (next: string, preview: boolean) => {
    if (preview) {
      const previous = previewUrl.current;
      if (
        previous &&
        previous !== next &&
        previous.startsWith("blob:") &&
        previous !== useUserStore.getState().developerProfile?.displayAvatar
      ) {
        URL.revokeObjectURL(previous);
      }
      previewUrl.current = next;
    } else {
      revokePreview();
    }
    setAvatar(next);
  };

  useEffect(() => {
    return () => {
      revokePreview();
      if (cropObjectUrl.current) URL.revokeObjectURL(cropObjectUrl.current);
    };
  }, []);

  useEffect(() => {
    ensureDeveloperProfile();
  }, []);

  useEffect(() => {
    if (seededRef.current) return;
    if (profileStatus === "idle" || profileStatus === "loading") return;
    seededRef.current = true;
    if (profileStatus === "error" || !developerProfile) {
      message.error(t("developerInfo.loadFailed"));
      setLoading(false);
      return;
    }
    imAvatarRef.current = developerProfile.imAvatarUrl;
    form.setFieldsValue({
      name: developerProfile.name,
      website: developerProfile.website,
    });
    if (!previewUrl.current) showAvatar(developerProfile.displayAvatar, false);
    setDeveloperNo(developerProfile.developerNo);
    setLoading(false);
  }, [developerProfile, form, profileStatus]);

  const closeCrop = () => {
    if (cropObjectUrl.current) {
      URL.revokeObjectURL(cropObjectUrl.current);
      cropObjectUrl.current = null;
    }
    setCropSrc(null);
  };

  const pickAvatar = (file: File) => {
    if (!AVATAR_TYPES.has(file.type)) {
      message.error(t("developerInfo.avatarType"));
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      message.error(t("developerInfo.avatarSize"));
      return;
    }
    if (cropObjectUrl.current) URL.revokeObjectURL(cropObjectUrl.current);
    const url = URL.createObjectURL(file);
    cropObjectUrl.current = url;
    setCropMime(file.type);
    setCropSrc(url);
  };

  const confirmCrop = (dataUrl: string) => {
    showAvatar(dataUrl, true);
    closeCrop();
  };

  const save = async (values: DeveloperFormValues) => {
    if (saving) return;
    const nextName = values.name.trim();
    const nextWebsite = values.website.trim();
    setSaving(true);
    try {
      const saved = await updateDeveloperProfile({
        name: nextName,
        website: nextWebsite,
        avatar: avatar === savedAvatar ? undefined : avatar,
      });
      const savedName = saved.developer_name?.trim() || nextName;
      const savedWebsite = saved.website_url?.trim() ?? nextWebsite;
      form.setFieldsValue({ name: savedName, website: savedWebsite });
      const nextProfile = {
        name: savedName,
        website: savedWebsite,
        developerNo,
        avatarUrl:
          saved.avatar_url !== undefined
            ? saved.avatar_url.trim()
            : (developerProfile?.avatarUrl ?? ""),
        imAvatarUrl: imAvatarRef.current,
      };
      let displayAvatar = avatar;
      if (saved.avatar_url !== undefined) {
        displayAvatar = await loadDeveloperAvatar(nextProfile);
        showAvatar(displayAvatar, false);
      }
      applyDeveloperProfile(
        toStoredProfile(nextProfile, displayAvatar),
        profileUserAvatar(nextProfile, displayAvatar),
      );
      message.success(t("developerInfo.saved"));
    } catch {
      message.error(t("developerInfo.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const copyId = async () => {
    const id = developerNo;
    if (!id) return;
    await navigator.clipboard?.writeText(id);
    message.success(t("common.copied"));
  };

  return (
    <Spin spinning={loading}>
      <div className="flex flex-col gap-8 p-8 m-5 border border-line rounded-lg bg-white">
        <h1 className="m-0 text-base font-medium text-primary">
          {t("developerInfo.title")}
        </h1>

        <div className="flex flex-col items-start gap-3">
          <Avatar src={avatar} size={80} className="border border-line" />
          <button
            type="button"
            className="border-0 bg-transparent p-0 text-sm text-accent cursor-pointer"
            onClick={() => fileRef.current?.click()}
          >
            {t("developerInfo.changeAvatar")}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) pickAvatar(file);
            }}
          />
        </div>
        <ImageCropModal
          open={Boolean(cropSrc)}
          src={cropSrc ?? ""}
          mime={cropMime}
          title={t("developerInfo.cropTitle")}
          cropShape="round"
          onCancel={closeCrop}
          onConfirm={confirmCrop}
        />

        <div className="h-px w-full bg-line" />

        <Form
          form={form}
          layout="vertical"
          validateTrigger="onBlur"
          initialValues={{
            name: developerProfile?.name ?? "",
            website: developerProfile?.website ?? "",
          }}
          onFinish={(values) => save(values)}
          className="flex flex-col gap-8 [&_.ant-form-item]:mb-0"
        >
          <Form.Item
            name="name"
            label={
              <span className="text-sm text-secondary">
                {t("developerInfo.name")}
              </span>
            }
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("developerInfo.nameRequired"),
              },
              { max: NAME_MAX, message: t("developerInfo.nameTooLong") },
            ]}
          >
            <Input maxLength={NAME_MAX} />
          </Form.Item>

          <Form.Item
            name="website"
            label={
              <span className="text-sm text-secondary">
                {t("developerInfo.consoleUrl")}
              </span>
            }
            rules={[
              {
                validator: async (_, value?: string) => {
                  const next = value?.trim() ?? "";
                  if (!next || /^https?:\/\//i.test(next)) return;
                  throw new Error(t("developerInfo.urlInvalid"));
                },
              },
            ]}
          >
            <Input
              onBlur={(event) => {
                const next = event.target.value.trim();
                form.setFieldValue("website", next);
                form.validateFields(["website"]);
              }}
            />
          </Form.Item>

          <div className="flex flex-col gap-2">
            <span className="text-sm text-secondary">
              {t("developerInfo.developerId")}
            </span>
            <div className="flex items-center gap-1">
              <span className="text-sm text-primary">{developerNo || "—"}</span>
              <IconBtn label={t("common.copy")} onClick={() => copyId()}>
                <CopyIcon />
              </IconBtn>
            </div>
          </div>

          <div className="h-px w-full bg-line" />
          <div className="flex items-center gap-2">
            {fromWorkbench && (
              <Button onClick={() => navigate("/apps")}>
                {t("developerInfo.back")}
              </Button>
            )}
            <BrandButton
              type="submit"
              disabled={!name.trim() || unchanged}
              loading={saving}
              className="self-start"
            >
              {t("common.save")}
            </BrandButton>
          </div>
        </Form>
      </div>
    </Spin>
  );
}

export default DeveloperInfo;
